import {
  isFilterBlock,
  isBetweenInclusive,
  getFilterOperator,
  isIndexedFilterBlock,
  getFilterValue,
  checkFilterQuerySync,
} from './../utils'
import type {RefObject} from 'react'
import {FilterQueryRequest} from '../filter-request'
import {
  type Parser,
  type AnyBlock,
  BlockType,
  type DelimiterLocator,
  type FilterProvider,
  type IndexedBlockValueItem,
  FilterOperator,
  type IndexedFilterBlock,
  ProviderSupportStatus,
  type FilterConfig,
} from '../types'
import {Strings} from '../constants/strings'

const SPACE_AND_QUOTES_REGEX = /\s(?=(?:[^"]*"[^"]*")*[^"]*$)/g
// trailing delimiter will be a colon, comma, or space
const TRAILING_DELIMITER_REGEX = /[:,\s]$/g
// a space, or a comma that is not inside of quotes
const NEXT_DELIMITER_REGEX = /\s|,(?=(?:[^"]*"[^"]*")*[^"]*$)/g
// a colon, comma, or space, followed by an optional operator, followed by the value
// visual chart: https://regexper.com/#%2F%28%5B%5Cs%2C%3A%22%5D*%3F%29%28%28%3F%3A%3E%7C%3C%7C%3E%3D%7C%3C%3D%29*%5B%5Cw%5C-_%22'%3F%2F%5C%5C.%3D%2B!%40%23%24%25%5E%26*%28%29%3B%5D*%29%24%2Fg
const LAST_DELIMITER_REGEX = /([\s,:"]*?)((?:>|<|>=|<=)*[\w\d\-_"'?/\\.=+!@#$%^&*();]*)$/g

export class DefaultParser implements Parser<FilterQueryRequest> {
  filterProviders: FilterProvider[] = []
  #inputRef: RefObject<HTMLInputElement>
  delimiter: DelimiterLocator | null
  config: FilterConfig
  #parsingCache: Record<string, FilterQueryRequest> = {}
  #validationCache: Set<string>

  constructor(inputRef: RefObject<HTMLInputElement>, config: FilterConfig) {
    this.#inputRef = inputRef
    this.config = config
    this.#validationCache = new Set()
  }

  parse(inputValue: string, filterQuery: FilterQueryRequest, caretIndex: number) {
    const blocks: AnyBlock[] = []

    let queries = []
    let remainingText = ''

    // Determine if there are uneven quotes
    if ((inputValue.match(/"/g) ?? []).length % 2 === 0) {
      // If not, split as normal
      queries = inputValue.split(SPACE_AND_QUOTES_REGEX)
    } else {
      // If so, split on the last quote and parse the part of the query before the last quote
      queries = inputValue.substring(0, inputValue.lastIndexOf('"')).split(SPACE_AND_QUOTES_REGEX)
      remainingText = inputValue.substring(inputValue.lastIndexOf('"'))

      // Check if the last query has a trailing delimiter
      if (queries.at(-1)?.match(TRAILING_DELIMITER_REGEX)?.length ?? 0 > 0) {
        let lastQuery = queries.pop() ?? ''
        remainingText = lastQuery.at(-1) + remainingText
        lastQuery = lastQuery?.replace(TRAILING_DELIMITER_REGEX, '')
        queries.push(lastQuery)
      }
    }

    let currentIndex = 0
    let blockIndex = 0
    let activeBlockId = -1

    for (const [entryIndex, query] of queries.entries()) {
      const filterDelimiterIndex = query.indexOf(this.config.filterDelimiter)
      const key = query.substring(0, filterDelimiterIndex)
      // Block index doubled to account for space elements

      const filterProvider = this.filterProviders.find(
        f =>
          f.key === key || f.aliases?.some(a => a === key) || (f.options?.filterTypes.exclusive && `-${f.key}` === key),
      )
      if (
        filterProvider &&
        (filterProvider.options?.filterTypes.multiKey !== false ||
          blocks.filter(b => isFilterBlock(b) && b.key.value === filterProvider.key).length < 1)
      ) {
        const block = this.processFilterBlock(
          query,
          filterProvider,
          currentIndex,
          filterDelimiterIndex,
          blockIndex,
          caretIndex,
        )
        if (isBetweenInclusive(caretIndex, currentIndex, currentIndex + query.length)) {
          activeBlockId = blockIndex
        }
        blocks.push(block)
        blockIndex++
      } else if (
        query !== '' ||
        (filterProvider &&
          !filterProvider.options?.filterTypes.multiKey &&
          blocks.filter(b => isFilterBlock(b) && b.key.value === filterProvider.key).length > 1)
      ) {
        if (blocks.at(-2)?.type === BlockType.Text && entryIndex < queries.length - 1) {
          const spaceBlock = blocks.pop()
          const prevBlock = blocks.pop()
          if (prevBlock) {
            prevBlock.raw += ` ${query}`
            currentIndex += query.length + 1
            blocks.push(prevBlock)
          }

          if (spaceBlock) {
            blocks.push(spaceBlock)
          }
          if (isBetweenInclusive(caretIndex, currentIndex, currentIndex + query.length)) {
            activeBlockId = blockIndex
          }
          continue
        }

        const block: AnyBlock = {id: blockIndex, type: BlockType.Text, raw: query}

        if (isBetweenInclusive(caretIndex, currentIndex, currentIndex + query.length)) {
          activeBlockId = blockIndex
        }
        blocks.push(block)
        blockIndex++
      }
      currentIndex += query.length

      // Add Space Element
      if (entryIndex < queries.length - 1) {
        currentIndex += 1
        // Don't need to set space as active block because it defaults to showing the filter keys
        blocks.push({id: blockIndex, type: BlockType.Space, raw: ' '})
        if (isBetweenInclusive(caretIndex, currentIndex, currentIndex + query.length)) {
          activeBlockId = blockIndex
        }
        blockIndex++
      }
    }

    if (remainingText.length > 0) {
      const remainingBlock = {id: blockIndex, type: BlockType.Text, raw: remainingText}
      blocks.push(remainingBlock)
    }

    if (!inputValue.endsWith(' ') && blocks.at(-1)?.type === BlockType.Space) {
      blocks.pop()
    }

    const newQuery = new FilterQueryRequest(inputValue, blocks, activeBlockId)
    newQuery.cachedFilterCount = filterQuery.cachedFilterCount
    return newQuery
  }

  insertSuggestion(filterBarQuery: FilterQueryRequest, suggestion: string, caretPosition: number): [string, number] {
    let newRaw: string
    let updatedCaretIndex = 0
    if (caretPosition < 0) {
      //If caret position is negative, then the cursor can't be found
      newRaw = filterBarQuery.raw + suggestion
      updatedCaretIndex = newRaw.length
    } else {
      let rawBefore = filterBarQuery.raw.substring(0, caretPosition)
      let rawAfter = filterBarQuery.raw.substring(caretPosition)
      NEXT_DELIMITER_REGEX.lastIndex = 0
      const match = NEXT_DELIMITER_REGEX.exec(rawAfter)

      if (match) {
        rawAfter = rawAfter.substring(match.index)
      } else {
        // This should only be when there are no blocks after
        rawAfter = ''
      }

      // Remove any partial value entered
      rawBefore = rawBefore.replace(LAST_DELIMITER_REGEX, `$1`) + suggestion
      newRaw = rawBefore + rawAfter
      updatedCaretIndex = rawBefore.length
    }
    return [newRaw, updatedCaretIndex]
  }

  getRaw(filterBarQuery: FilterQueryRequest): string {
    let raw = ''
    for (const block of filterBarQuery.blocks) {
      if (block.type === BlockType.Space) {
        raw += ' '
      } else if (isFilterBlock(block)) {
        raw += block.key.value
        raw += block.value.raw ? `:${block.value.raw}` : ''
      } else {
        raw += block.raw
      }
    }
    return raw
  }

  validateFilterProvider(filterProvider: FilterProvider): [boolean, string | undefined] {
    if (filterProvider.options?.support.status === ProviderSupportStatus.Unsupported) {
      return [false, filterProvider.options?.support.message ?? Strings.filterProviderNotSupported(filterProvider.key)]
    } else if (filterProvider.options?.support.status === ProviderSupportStatus.Deprecated) {
      return [false, filterProvider.options?.support.message ?? Strings.filterProviderDeprecated(filterProvider.key)]
    }
    return [true, undefined]
  }

  processFilterBlock(
    filterString: string,
    filterProvider: FilterProvider,
    startIndex: number,
    filterDelimiterIndex: number,
    blockIndex: number,
    caretIndex?: number,
  ): IndexedFilterBlock {
    const keyValue = filterString.substring(0, filterDelimiterIndex)
    const valueString = filterString.substring(filterDelimiterIndex + 1)
    const operator = getFilterOperator(filterProvider, keyValue, valueString) ?? FilterOperator.Is
    let runningIndex = startIndex + filterDelimiterIndex + 1

    let blockValueItems = valueString.split(/,(?=(?:[^"]*"[^"]*")*[^"]*$)/g)

    if (operator === FilterOperator.Between) {
      let splitValues: string[] = []
      blockValueItems.map(n => {
        let vals = n.split(/(.*)\.\.(.*)/g).filter(v => v)
        if (vals.length < 2) {
          if (n.startsWith('..')) {
            vals = ['', ...vals]
          } else {
            vals.push('')
          }
        }

        splitValues = [...splitValues, ...vals]
      })

      blockValueItems = splitValues
    }

    const values = blockValueItems.map(v => {
      const valueObject: IndexedBlockValueItem = {
        startIndex: runningIndex,
        endIndex: runningIndex + v.length,
        hasCaret: isBetweenInclusive(
          caretIndex ?? this.#inputRef.current?.selectionStart ?? -1,
          runningIndex,
          runningIndex + v.length,
        ),
        value: v,
        valid: this.#validationCache.has(`${keyValue}:${getFilterValue(v)}`) ? true : undefined,
      }

      // Accounts for 2 spaces for `..` for Between, 1 space for `,`
      const valueDelimiterSize = operator === FilterOperator.Between ? 2 : 1

      runningIndex = runningIndex + v.length + valueDelimiterSize
      return valueObject
    })

    const hasCaret = isBetweenInclusive(caretIndex ?? -1, startIndex, startIndex + filterString.length)

    const filterBlock: IndexedFilterBlock = {
      id: blockIndex,
      type: BlockType.Filter,
      provider: filterProvider,
      operator,
      startIndex,
      endIndex: startIndex + filterString.length,
      hasCaret,
      valid: this.#validationCache.has(filterString) ? true : undefined,
      key: {
        startIndex,
        endIndex: startIndex + filterDelimiterIndex,
        hasCaret: isBetweenInclusive(
          this.#inputRef.current?.selectionStart ?? -1,
          startIndex,
          startIndex + filterDelimiterIndex,
        ),
        value: keyValue,
      },
      value: {
        startIndex: startIndex + filterDelimiterIndex + 1,
        endIndex: startIndex + filterString.length,
        values,
        raw: valueString,
      },
      raw: filterString,
    }

    return filterBlock
  }

  async validateFilterQuery(filterQuery: FilterQueryRequest): Promise<FilterQueryRequest> {
    const blockPromises = filterQuery.blocks.map(async block => {
      if (isIndexedFilterBlock(block)) {
        const [providerValid, providerValidationMessage] = this.validateFilterProvider(block.provider)

        const values = await block.provider.validateFilterBlockValues?.(block.value.values, block, this.config)

        //Cache validation
        values.map(async v => {
          if (v.valid) {
            this.#validationCache.add(`${block.provider.key}:${getFilterValue(v.value)}`)
          }
        })

        const isBlockValid = values.every(v => v.valid) && providerValid

        isBlockValid && this.#validationCache.add(block.raw)

        return {
          ...block,
          valid: isBlockValid,
          key: {
            ...block.key,
            valid: providerValid,
            validationMessage: providerValidationMessage,
          },
          value: {
            ...block.value,
            values,
          },
        }
      }

      return block
    })

    const blocks = await Promise.all(blockPromises)

    if (!this.#parsingCache[filterQuery.raw] && checkFilterQuerySync(filterQuery))
      this.#parsingCache[filterQuery.raw] = filterQuery

    const newQuery = new FilterQueryRequest(filterQuery.raw, blocks, filterQuery.activeBlockId, true)
    newQuery.cachedFilterCount = filterQuery.cachedFilterCount
    return newQuery
  }
}
